import {
  AreaChart,
  CalendarCheck,
  CheckSquare,
  Settings2,
  Square,
} from "@tamagui/lucide-icons";
import type { Timestamp } from "firebase/firestore";
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import { TKey } from "../../../shared/translations/translationKeys";
import { ModuleType } from "../../../templates/types";
import { ActivityEventType } from "../../activities/types";
import type { Api } from "../../core/apis";
import { showConfetti } from "../../core/states.ts/showConfetti";
import type { Stores } from "../../core/stores";
import type UserModel from "../../users/UserModel";
import { ModuleDataType } from "../core/BaseModuleDataModel";
import BaseModuleModel, {
  type MenuOption,
  type ModuleModelData,
} from "../core/BaseModuleModel";
import { getDefaultSettingsForModule } from "../utils";
import TrackerUserData, {
  type TrackerHistoryItem,
  type TrackerUserDataProps,
} from "./data/TrackerUserData";
import { TrackerModuleBlockUI } from "./ui/TrackerModuleBlockUI";
import { TrackerModuleUI } from "./ui/TrackerModuleUI";

export type TrackerLeaderboardItem = {
  user?: UserModel;
  totalCount: number;
  totalEntries: number;
  lastComment?: string;
};

export type TrackerModuleSnapshot = {
  totalStatsCount: number;
  totalParticipants: number;
};

export type TrackerOption = {
  uid: string;
  label: string;
  weight: number;
  isDynamic: boolean; // if amount can be applied or not
  isDeleted: boolean;
};

export type TrackerModuleSettings = {
  options: TrackerOption[];
  useBucketMode: boolean;
  useDescription: boolean;
  useDailyLimit: number | null;

  dateStart: Timestamp | null;
  dateEnd: Timestamp | null;
};

export interface TrackerModuleModelData
  extends ModuleModelData<TrackerModuleSnapshot> {
  settings: TrackerModuleSettings;

  description: string;
}

export default class TrackerModuleModel extends BaseModuleModel<
  TrackerModuleSnapshot,
  TrackerModuleModelData
> {
  constructor(stores: Stores, api: Api, data: TrackerModuleModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      description: data.description ?? "",
      settings: getDefaultSettingsForModule(ModuleType.Tracker, data.settings),
    });

    makeObservable(this);
  }

  @observable showOptionsModal = false;
  @action setShowOptionsModal(state: boolean) {
    this.showOptionsModal = state;
  }

  @computed get totalStatsCount(): number {
    let total = 0;
    for (const u of this.userData) {
      total += u.data.history.length;
    }

    return total;
  }

  @computed get hasStarted() {
    if (
      this.data.settings.dateStart &&
      this.data.settings.dateStart.toMillis() > Date.now()
    ) {
      return false;
    }
    return true;
  }

  get realDateEnd(): Date | undefined {
    if (this.data.settings.dateEnd) {
      // Get the start of the next day (midnight of the next day)
      const tomorrow = new Date(this.data.settings.dateEnd.toMillis());
      tomorrow.setHours(0, 0, 0, 0);
      tomorrow.setDate(tomorrow.getDate() + 1);

      return tomorrow;
    }
    return undefined;
  }

  @computed get hasEnded() {
    const realDateEnd = this.realDateEnd;
    if (realDateEnd && realDateEnd.getTime() <= Date.now()) {
      return true;
    }
    return false;
  }
  @computed get isCurrentlyRunning(): boolean {
    if (!this.hasStarted || this.hasEnded) {
      return false;
    }

    return true;
  }

  @action async saveSettings(s: Partial<TrackerModuleSettings>) {
    const newSettings = { ...this.data.settings, ...s };
    this.data.settings = newSettings;
    await this.save();
  }

  @action async updateSnapshotWithoutSave() {
    this.data.snapshot = {
      totalStatsCount: this.totalStatsCount,
      totalParticipants: this.userData.length,
    };
  }

  @action async saveOptions(options: TrackerOption[]) {
    this.data.settings.options = options;
    await this.save();
  }

  @computed get userData(): TrackerUserData[] {
    return this.dataItems.filter(
      (item): item is TrackerUserData =>
        TrackerUserData.is(item) && item.data.history.length > 0,
    );
  }

  @computed get combinedHistory(): TrackerHistoryItem[] {
    const items: TrackerHistoryItem[] = [];

    for (const uData of this.userData) {
      items.push(...uData.data.history);
    }

    return items.sort((a, b) => (a.ts < b.ts ? -1 : 1));
  }

  @computed get entriesFromToday() {
    if (!this.myUserData) {
      return [];
    }

    const ts = Date.now();

    const getStartOfDay = (timestamp: number): number => {
      const date = new Date(timestamp);
      date.setHours(0, 0, 0, 0);
      return date.getTime();
    };

    const getEndOfDay = (timestamp: number): number => {
      const date = new Date(timestamp);
      date.setHours(23, 59, 59, 999);
      return date.getTime();
    };

    const startOfDay = getStartOfDay(ts);
    const endOfDay = getEndOfDay(ts);

    return this.myUserData.data.history.filter((item) => {
      return item.ts >= startOfDay && item.ts <= endOfDay;
    });
  }

  @computed get dailyEntryLimitReached(): boolean {
    if (
      !this.data.settings.useDailyLimit ||
      this.data.settings.useDailyLimit === 0
    ) {
      return false;
    }

    return this.entriesFromToday.length >= this.data.settings.useDailyLimit;
  }

  @computed get user(): UserModel | undefined {
    return this.plan.userInPlan;
  }

  @computed get hasData(): boolean {
    return this.leaderboard.length > 0;
  }

  @computed get myUserData(): TrackerUserData | undefined {
    const user = this.user;

    if (!user) {
      return undefined;
    }

    return this.userData.find((item) => item.data.userUid === user.uid);
  }

  @computed get currentOptionsInBucket(): TrackerOption[] {
    const data = this.myUserData?.data.history;

    const currentBucket: TrackerOption[] = [];
    const allOptions = this.activeOptions;

    if (!data?.length) {
      return [];
    }

    for (const d of data) {
      if (!currentBucket.find((b) => b.uid === d.type)) {
        const o = this.getOption(d.type);
        if (o) {
          currentBucket.push(o);
          if (currentBucket.length === allOptions.length) {
            currentBucket.length = 0; // full, reset it
          }
        }
      }
    }

    return currentBucket;
  }

  @action getOption(type: string) {
    return this.data.settings.options.find((o) => o.uid === type);
  }

  @action getUserData(userUid: string) {
    return this.userData.find((item) => item.data.userUid === userUid);
  }

  private calculateLeaderboardData(
    entry: TrackerUserData,
    optionsMap: Map<string, TrackerOption>,
  ): TrackerLeaderboardItem {
    const user = this.stores.usersStore.byId.get(entry.data.userUid);

    const i: TrackerLeaderboardItem = {
      user,
      totalCount: 0,
      totalEntries: 0,
      lastComment: entry.lastEntryComment,
    };

    entry.data.history.forEach((h) => {
      const o = optionsMap.get(h.type);
      if (o) {
        const realAmount = o.isDynamic ? h.amount : 1;

        i.totalCount += realAmount * o.weight;
        i.totalEntries++;
      }
    });

    return i;
  }

  @computed get activeOptions() {
    return this.data.settings.options.filter((o) => !o.isDeleted);
  }

  @computed get selectableOptions() {
    if (!this.data.settings.useBucketMode) {
      return this.activeOptions;
    }

    return this.activeOptions.filter(
      (o) => !this.currentOptionsInBucket.find((c) => c.uid === o.uid),
    );
  }

  @computed get leaderboard(): TrackerLeaderboardItem[] {
    const optionsMap = new Map<string, TrackerOption>(
      this.data.settings.options.map((o) => {
        return [o.uid, o];
      }),
    );

    const dataSorted = this.userData
      .map((u) => this.calculateLeaderboardData(u, optionsMap))
      .filter((u) => u.totalCount > 0)
      .sort((a, b) => (a.totalCount < b.totalCount ? 1 : -1));

    return dataSorted;
  }

  @action async addEntry(inputData: Partial<TrackerHistoryItem>) {
    // Update or create a new entry for this user

    if (!this.userUid || this.dailyEntryLimitReached) {
      return;
    }

    const isExisting = inputData.ts;
    if (!inputData.ts) {
      delete inputData.ts;
    }

    const data: TrackerHistoryItem = {
      ts: Date.now(),
      amount: 1,
      type: "",
      comment: "",
      userUid: this.userUid,
      ...inputData,
    };

    const option = this.getOption(data.type);

    const existingData = this.myUserData;

    if (existingData) {
      if (isExisting) {
        await existingData.updateEntry(data);
      } else {
        await existingData.addEntry(data);

        this.postActivityEvent(
          ActivityEventType.DataAdded,
          TKey.ActivityTrackerItemAdded,
          {
            comment: data.comment,
            amount: data.amount ?? 1,
            type: option?.label ?? "-",
          },
          existingData.uid,
        );
      }
    } else {
      await this.addNewData<TrackerUserDataProps>(
        ModuleDataType.TrackerItem,
        { userUid: this.user?.uid, history: [data] },
        TKey.ActivityTrackerItemAdded,
        {
          comment: data.comment,
          amount: data.amount ?? 1,
          type: option?.label ?? "-",
        },
      );
    }

    await this.updateSnapshotWithoutSave();

    showConfetti();
  }

  @observable showDatesFormSheet = false;

  @action toggleDatesFormSheet() {
    this.showDatesFormSheet = !this.showDatesFormSheet;
    this.setActivitySheetOpen(!this.showDatesFormSheet);
  }

  render() {
    return <TrackerModuleUI module={this} />;
  }

  renderStatic() {
    return null;
  }

  renderBlock() {
    return <TrackerModuleBlockUI module={this} />;
  }

  renderStaticBlock() {
    return this.renderBlock();
  }

  renderIcon(size?: number) {
    return <AreaChart size={size} />;
  }

  renderMenuOptions(): MenuOption[] {
    return [
      {
        icon: Settings2,
        label: "Manage options",
        onPress: () => {
          this.setShowOptionsModal(!this.showOptionsModal);
        },
      },

      {
        icon: this.data.settings.useDescription ? CheckSquare : Square,
        label: "Show rules area",
        onPress: () => {
          this.saveSettings({
            useDescription: !this.data.settings.useDescription,
          });
        },
      },

      {
        icon: CalendarCheck,
        label: "Set dates",
        onPress: () => {
          runInAction(() => {
            this.toggleDatesFormSheet();
          });
        },
      },
    ];
  }
}
